/**
 * Complete catalog of Confluence + Jira MCP tools.
 * Used by the AgentFront orchestrator and the slash command palette.
 */

export type McpService =
  | 'confluence'
  | 'jira'
  | 'slack'
  | 'google'
  | 'google-mcp'
  | 'notion'
export type HttpMethod = 'GET' | 'POST' | 'PUT'

// Basic parameter definition used by tools
export interface ToolParam {
  name: string
  type: string
  required: boolean
  description?: string
}

// Definition of a tool in the catalog
export interface McpTool {
  name: string
  service: McpService
  method: HttpMethod
  path: string
  description: string
  category: string
  slashCommand: string
  paramsIn: string
  params: ToolParam[]
}

// System command definition (e.g., internal actions)
export interface SystemCommand {
  name: string
  description: string
  slashCommand: string
}

// ─── Confluence Tools ─────────────────────────────────────────────────────────

const confluenceTools: McpTool[] = [
  // Spaces
  {
    name: 'confluence_get_spaces',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_spaces',
    description: 'Listar espacios de Confluence',
    category: 'Spaces',
    slashCommand: '/spaces',
    paramsIn: 'body',
    params: [
      {
        name: 'type',
        type: 'string',
        required: false,
        description: 'Tipo de espacio',
        example: 'global',
      },
      {
        name: 'status',
        type: 'string',
        required: false,
        description: 'Estado',
        example: 'current',
      },
      {
        name: 'limit',
        type: 'number',
        required: false,
        description: 'Límite de resultados',
        example: '100',
      },
    ],
  },
  {
    name: 'confluence_get_space',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_space',
    description: 'Obtener un espacio por key o id',
    category: 'Spaces',
    slashCommand: '/space',
    paramsIn: 'body',
    params: [
      {
        name: 'idOrKey',
        type: 'string',
        required: true,
        description: 'Key o ID del espacio',
        example: 'DEMO',
      },
    ],
  },
  {
    name: 'confluence_create_space',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_create_space',
    description: 'Crear un nuevo espacio',
    category: 'Spaces',
    slashCommand: '/create-space',
    paramsIn: 'body',
    params: [
      {
        name: 'key',
        type: 'string',
        required: true,
        description: 'Key del espacio',
        example: 'NEWSPACE',
      },
      {
        name: 'name',
        type: 'string',
        required: true,
        description: 'Nombre',
        example: 'New Space',
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Descripción',
      },
      {
        name: 'type',
        type: 'string',
        required: false,
        description: 'Tipo',
        example: 'global',
      },
    ],
  },
  // Pages
  {
    name: 'confluence_get_page',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_page',
    description: 'Obtener página por ID',
    category: 'Pages',
    slashCommand: '/page',
    paramsIn: 'body',
    params: [
      {
        name: 'pageId',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
      {
        name: 'bodyFormat',
        type: 'string',
        required: false,
        description: 'Formato del body',
        example: 'storage',
      },
    ],
  },
  {
    name: 'confluence_get_page_by_title',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_page_by_title',
    description: 'Buscar página por título en un espacio',
    category: 'Pages',
    slashCommand: '/page-by-title',
    paramsIn: 'body',
    params: [
      {
        name: 'spaceKey',
        type: 'string',
        required: true,
        description: 'Key del espacio',
      },
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Título de la página',
      },
    ],
  },
  {
    name: 'confluence_create_page',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_create_page',
    description: 'Crear una nueva página',
    category: 'Pages',
    slashCommand: '/create-page',
    paramsIn: 'body',
    params: [
      {
        name: 'spaceId',
        type: 'string',
        required: true,
        description: 'ID del espacio',
      },
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Título',
      },
      {
        name: 'body',
        type: 'string',
        required: true,
        description: 'Contenido HTML',
      },
      {
        name: 'parentId',
        type: 'string',
        required: false,
        description: 'ID de la página padre',
      },
    ],
  },
  {
    name: 'confluence_update_page',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_update_page',
    description: 'Actualizar una página existente',
    category: 'Pages',
    slashCommand: '/update-page',
    paramsIn: 'body',
    params: [
      {
        name: 'pageId',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
      {
        name: 'versionNumber',
        type: 'number',
        required: true,
        description: 'Número de versión',
      },
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Título',
      },
      {
        name: 'body',
        type: 'string',
        required: true,
        description: 'Contenido HTML',
      },
    ],
  },
  {
    name: 'confluence_get_page_children',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_page_children',
    description: 'Listar hijos directos de una página',
    category: 'Pages',
    slashCommand: '/page-children',
    paramsIn: 'body',
    params: [
      {
        name: 'pageId',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
      {
        name: 'limit',
        type: 'number',
        required: false,
        description: 'Límite',
      },
    ],
  },
  {
    name: 'confluence_get_page_ancestors',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_page_ancestors',
    description: 'Obtener ancestros (breadcrumb)',
    category: 'Pages',
    slashCommand: '/page-ancestors',
    paramsIn: 'body',
    params: [
      {
        name: 'pageId',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
    ],
  },
  {
    name: 'confluence_get_pages_in_space',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_pages_in_space',
    description: 'Listar páginas en un espacio',
    category: 'Pages',
    slashCommand: '/pages',
    paramsIn: 'body',
    params: [
      {
        name: 'spaceId',
        type: 'string',
        required: true,
        description: 'ID del espacio',
      },
      {
        name: 'limit',
        type: 'number',
        required: false,
        description: 'Límite',
      },
      {
        name: 'sort',
        type: 'string',
        required: false,
        description: 'Ordenar por',
        example: 'modified-date',
      },
    ],
  },
  {
    name: 'confluence_get_page_with_descendants',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_page_with_descendants',
    description: 'Página y descendientes hasta maxDepth',
    category: 'Pages',
    slashCommand: '/page-tree',
    paramsIn: 'body',
    params: [
      {
        name: 'pageId',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
      {
        name: 'maxDepth',
        type: 'number',
        required: false,
        description: 'Profundidad máxima',
      },
      {
        name: 'bodyFormat',
        type: 'string',
        required: false,
        description: 'Formato',
        example: 'storage',
      },
    ],
  },
  // Search
  {
    name: 'confluence_search_cql',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_search_cql',
    description: 'Búsqueda con CQL',
    category: 'Search',
    slashCommand: '/search-cql',
    paramsIn: 'body',
    params: [
      {
        name: 'cql',
        type: 'string',
        required: true,
        description: 'Query CQL',
        example: 'space = DEMO',
      },
      {
        name: 'limit',
        type: 'number',
        required: false,
        description: 'Límite',
      },
    ],
  },
  {
    name: 'confluence_search_content',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_search_content',
    description: 'Búsqueda por texto',
    category: 'Search',
    slashCommand: '/search',
    paramsIn: 'body',
    params: [
      {
        name: 'query',
        type: 'string',
        required: true,
        description: 'Texto a buscar',
      },
      {
        name: 'spaceKey',
        type: 'string',
        required: false,
        description: 'Filtrar por espacio',
      },
      {
        name: 'limit',
        type: 'number',
        required: false,
        description: 'Límite',
      },
    ],
  },
  // Labels & Comments
  {
    name: 'confluence_get_page_labels',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_page_labels',
    description: 'Obtener labels de una página',
    category: 'Labels',
    slashCommand: '/labels',
    paramsIn: 'body',
    params: [
      {
        name: 'contentId',
        type: 'string',
        required: true,
        description: 'ID del contenido',
      },
    ],
  },
  {
    name: 'confluence_add_page_label',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_add_page_label',
    description: 'Agregar label a una página',
    category: 'Labels',
    slashCommand: '/add-label',
    paramsIn: 'body',
    params: [
      {
        name: 'contentId',
        type: 'string',
        required: true,
        description: 'ID del contenido',
      },
      {
        name: 'labelName',
        type: 'string',
        required: true,
        description: 'Nombre del label',
      },
    ],
  },
  {
    name: 'confluence_get_page_comments',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_page_comments',
    description: 'Listar comentarios de una página',
    category: 'Comments',
    slashCommand: '/comments',
    paramsIn: 'body',
    params: [
      {
        name: 'pageId',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
      {
        name: 'limit',
        type: 'number',
        required: false,
        description: 'Límite',
      },
    ],
  },
  {
    name: 'confluence_create_page_comment',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_create_page_comment',
    description: 'Crear comentario en una página',
    category: 'Comments',
    slashCommand: '/add-comment-page',
    paramsIn: 'body',
    params: [
      {
        name: 'pageId',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
      {
        name: 'body',
        type: 'string',
        required: true,
        description: 'Contenido HTML',
      },
      {
        name: 'parentId',
        type: 'string',
        required: false,
        description: 'ID del comentario padre',
      },
    ],
  },
  // Attachments
  {
    name: 'confluence_get_attachments',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_get_attachments',
    description: 'Listar adjuntos de una página',
    category: 'Attachments',
    slashCommand: '/attachments',
    paramsIn: 'body',
    params: [
      {
        name: 'pageId',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
    ],
  },
  {
    name: 'confluence_upload_attachment',
    service: 'confluence',
    method: 'POST',
    path: '/tools/confluence_upload_attachment',
    description: 'Subir adjunto (content en base64)',
    category: 'Attachments',
    slashCommand: '/upload-attachment',
    paramsIn: 'body',
    params: [
      {
        name: 'pageId',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
      {
        name: 'filename',
        type: 'string',
        required: true,
        description: 'Nombre del archivo',
      },
      {
        name: 'content',
        type: 'string',
        required: true,
        description: 'Contenido en base64',
      },
      {
        name: 'contentType',
        type: 'string',
        required: true,
        description: 'MIME type',
        example: 'text/plain',
      },
    ],
  },
]

// ─── Jira Tools ───────────────────────────────────────────────────────────────

const jiraTools: McpTool[] = [
  // Agile — Boards & Sprints (all POST in new MCP API)
  {
    name: 'agile-get-boards',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-get-boards',
    description: 'Listar boards de Jira (Scrum/Kanban)',
    category: 'Boards',
    slashCommand: '/boards',
    paramsIn: 'body',
    params: [
      {
        name: 'projectKey',
        type: 'string',
        required: false,
        description: 'Filtrar por proyecto',
        example: 'TALB',
      },
    ],
  },
  {
    name: 'agile-get-sprints',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-get-sprints',
    description: 'Listar sprints de un board',
    category: 'Boards',
    slashCommand: '/sprints',
    paramsIn: 'body',
    params: [
      {
        name: 'board_id',
        type: 'string',
        required: true,
        description: 'ID del board',
        example: '1',
      },
      {
        name: 'state',
        type: 'string',
        required: false,
        description: 'Estado del sprint (active, closed, future)',
        example: 'active',
      },
    ],
  },
  {
    name: 'agile-get-active-sprints',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-get-active-sprints',
    description: 'Obtener sprints activos (de un board o todos)',
    category: 'Boards',
    slashCommand: '/active-sprints',
    paramsIn: 'body',
    params: [
      {
        name: 'board_id',
        type: 'string',
        required: false,
        description: 'ID del board (opcional, si no se pasa devuelve todos)',
      },
    ],
  },
  {
    name: 'agile-get-sprint',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-get-sprint',
    description: 'Obtener detalle de un sprint',
    category: 'Boards',
    slashCommand: '/sprint',
    paramsIn: 'body',
    params: [
      {
        name: 'sprint_id',
        type: 'string',
        required: true,
        description: 'ID del sprint',
      },
    ],
  },
  {
    name: 'agile-get-sprint-issues',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-get-sprint-issues',
    description: 'Obtener issues de un sprint',
    category: 'Boards',
    slashCommand: '/sprint-issues',
    paramsIn: 'body',
    params: [
      {
        name: 'board_id',
        type: 'string',
        required: true,
        description: 'ID del board',
      },
      {
        name: 'sprint_id',
        type: 'string',
        required: true,
        description: 'ID del sprint',
      },
    ],
  },
  {
    name: 'agile-get-backlog',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-get-backlog',
    description: 'Obtener issues en backlog de un board',
    category: 'Boards',
    slashCommand: '/backlog',
    paramsIn: 'body',
    params: [
      {
        name: 'board_id',
        type: 'string',
        required: true,
        description: 'ID del board',
      },
    ],
  },
  {
    name: 'agile-delete-sprint',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-delete-sprint',
    description: 'Eliminar un sprint (solo no iniciados o cerrados)',
    category: 'Boards',
    slashCommand: '/delete-sprint',
    paramsIn: 'body',
    params: [
      {
        name: 'sprint_id',
        type: 'string',
        required: true,
        description: 'ID del sprint',
      },
    ],
  },
  {
    name: 'agile-delete-board',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-delete-board',
    description: 'Eliminar un board',
    category: 'Boards',
    slashCommand: '/delete-board',
    paramsIn: 'body',
    params: [
      {
        name: 'board_id',
        type: 'string',
        required: true,
        description: 'ID del board',
      },
    ],
  },
  {
    name: 'agile-create-sprint',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-create-sprint',
    description: 'Crear un nuevo sprint en un board',
    category: 'Boards',
    slashCommand: '/create-sprint',
    paramsIn: 'body',
    params: [
      {
        name: 'board_id',
        type: 'string',
        required: true,
        description: 'ID del board',
      },
      {
        name: 'name',
        type: 'string',
        required: true,
        description: 'Nombre del sprint',
      },
      {
        name: 'start_date',
        type: 'string',
        required: false,
        description: 'Fecha de inicio ISO-8601 (ej: 2026-03-10T00:00:00.000Z)',
      },
      {
        name: 'end_date',
        type: 'string',
        required: false,
        description: 'Fecha de fin ISO-8601',
      },
      {
        name: 'goal',
        type: 'string',
        required: false,
        description: 'Objetivo del sprint',
      },
    ],
  },
  {
    name: 'agile-update-sprint',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-update-sprint',
    description: 'Actualizar nombre, goal, fechas o estado de un sprint',
    category: 'Boards',
    slashCommand: '/update-sprint',
    paramsIn: 'body',
    params: [
      {
        name: 'sprint_id',
        type: 'string',
        required: true,
        description: 'ID del sprint',
      },
      {
        name: 'name',
        type: 'string',
        required: false,
        description: 'Nuevo nombre',
      },
      {
        name: 'state',
        type: 'string',
        required: false,
        description: 'Nuevo estado: future, active, closed',
      },
      {
        name: 'start_date',
        type: 'string',
        required: false,
        description: 'Nueva fecha de inicio ISO-8601',
      },
      {
        name: 'end_date',
        type: 'string',
        required: false,
        description: 'Nueva fecha de fin ISO-8601',
      },
      {
        name: 'goal',
        type: 'string',
        required: false,
        description: 'Nuevo objetivo',
      },
    ],
  },
  {
    name: 'agile-move-issues-to-sprint',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-move-issues-to-sprint',
    description: 'Mover issues a un sprint',
    category: 'Boards',
    slashCommand: '/move-to-sprint',
    paramsIn: 'body',
    params: [
      {
        name: 'sprint_id',
        type: 'string',
        required: true,
        description: 'ID del sprint destino',
      },
      {
        name: 'issue_keys',
        type: 'string[]',
        required: true,
        description: 'Keys de los issues a mover',
      },
    ],
  },
  {
    name: 'agile-move-issues-to-backlog',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-move-issues-to-backlog',
    description: 'Mover issues al backlog',
    category: 'Boards',
    slashCommand: '/move-to-backlog',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_keys',
        type: 'string[]',
        required: true,
        description: 'Keys de los issues a mover al backlog',
      },
    ],
  },
  // Board Configuration (new endpoints)
  {
    name: 'agile-get-board-configuration',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-get-board-configuration',
    description:
      'Obtener configuración completa de un board (columnas, estimación, ranking)',
    category: 'Boards',
    slashCommand: '/board-config',
    paramsIn: 'body',
    params: [
      {
        name: 'boardId',
        type: 'number',
        required: true,
        description: 'ID del board',
      },
    ],
  },
  {
    name: 'agile-get-board-columns',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-get-board-columns',
    description:
      'Obtener configuración de columnas (estados por columna) de un board',
    category: 'Boards',
    slashCommand: '/board-columns',
    paramsIn: 'body',
    params: [
      {
        name: 'boardId',
        type: 'number',
        required: true,
        description: 'ID del board',
      },
    ],
  },
  {
    name: 'agile-get-board-filter',
    service: 'jira',
    method: 'POST',
    path: '/tools/agile-get-board-filter',
    description: 'Obtener el filtro JQL asociado a un board',
    category: 'Boards',
    slashCommand: '/board-filter',
    paramsIn: 'body',
    params: [
      {
        name: 'boardId',
        type: 'number',
        required: true,
        description: 'ID del board',
      },
    ],
  },
  // Search & Read
  {
    name: 'search-issues',
    service: 'jira',
    method: 'POST',
    path: '/tools/search-issues',
    description: 'Buscar issues con JQL',
    category: 'Search',
    slashCommand: '/issues',
    paramsIn: 'body',
    params: [
      {
        name: 'jql',
        type: 'string',
        required: true,
        description: 'Query JQL',
        example: 'project = PIAE AND issuetype = Epic',
      },
      {
        name: 'max_results',
        type: 'number',
        required: false,
        description: 'Máximo de resultados',
        example: '50',
      },
    ],
  },
  {
    name: 'get-issue',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-issue',
    description: 'Obtener un issue por key',
    category: 'Search',
    slashCommand: '/issue',
    paramsIn: 'query',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
        example: 'PIAE-1',
      },
    ],
  },
  {
    name: 'get-transitions',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-transitions',
    description: 'Obtener transiciones disponibles de un issue',
    category: 'Search',
    slashCommand: '/transitions',
    paramsIn: 'query',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
    ],
  },
  {
    name: 'get-custom-fields',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-custom-fields',
    description: 'Obtener campos custom de un proyecto',
    category: 'Admin',
    slashCommand: '/custom-fields',
    paramsIn: 'query',
    params: [
      {
        name: 'project',
        type: 'string',
        required: true,
        description: 'Key del proyecto',
      },
    ],
  },
  {
    name: 'get-users',
    service: 'jira',
    method: 'POST',
    path: '/tools/get-users',
    description: 'Buscar usuarios',
    category: 'Admin',
    slashCommand: '/users',
    paramsIn: 'body',
    params: [
      {
        name: 'query',
        type: 'string',
        required: true,
        description: 'Texto de búsqueda',
      },
      {
        name: 'max_results',
        type: 'number',
        required: false,
        description: 'Máximo',
      },
    ],
  },
  // Changelog & Transitions History
  {
    name: 'get-issue-changelog',
    service: 'jira',
    method: 'POST',
    path: '/tools/get-issue-changelog',
    description:
      'Historial completo de cambios de un issue (incluye transiciones)',
    category: 'Search',
    slashCommand: '/changelog',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
    ],
  },
  {
    name: 'get-issue-transitions-history',
    service: 'jira',
    method: 'POST',
    path: '/tools/get-issue-transitions-history',
    description:
      'Historial de cambios de estado (quién, cuándo, de → a, tiempo en estado)',
    category: 'Search',
    slashCommand: '/transitions-history',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
    ],
  },
  // Deprecated Issues
  {
    name: 'get-deprecated-issues',
    service: 'jira',
    method: 'POST',
    path: '/tools/get-deprecated-issues',
    description:
      'Listar issues deprecados (label=deprecated o status=Deprecated)',
    category: 'Search',
    slashCommand: '/deprecated',
    paramsIn: 'body',
    params: [
      {
        name: 'project',
        type: 'string',
        required: false,
        description: 'Key del proyecto (opcional)',
      },
    ],
  },
  {
    name: 'deprecate-issue',
    service: 'jira',
    method: 'POST',
    path: '/tools/deprecate-issue',
    description: 'Marcar issue como deprecado (añadir label y/o transicionar)',
    category: 'Update',
    slashCommand: '/deprecate',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'add_label',
        type: 'string',
        required: false,
        description: 'Label a agregar',
        example: 'deprecated',
      },
    ],
  },
  // Create
  {
    name: 'create-epic',
    service: 'jira',
    method: 'POST',
    path: '/tools/create-epic',
    description: 'Crear una épica',
    category: 'Create',
    slashCommand: '/create-epic',
    paramsIn: 'body',
    params: [
      {
        name: 'project',
        type: 'string',
        required: true,
        description: 'Key del proyecto',
      },
      {
        name: 'summary',
        type: 'string',
        required: true,
        description: 'Título',
      },
      {
        name: 'epic_name',
        type: 'string',
        required: true,
        description: 'Nombre de la épica',
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Descripción',
      },
      {
        name: 'priority',
        type: 'string',
        required: false,
        description: 'Prioridad',
        example: 'Medium',
      },
    ],
  },
  {
    name: 'create-story',
    service: 'jira',
    method: 'POST',
    path: '/tools/create-story',
    description: 'Crear una story',
    category: 'Create',
    slashCommand: '/create-story',
    paramsIn: 'body',
    params: [
      {
        name: 'project',
        type: 'string',
        required: true,
        description: 'Key del proyecto',
      },
      {
        name: 'summary',
        type: 'string',
        required: true,
        description: 'Título',
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Descripción',
      },
      {
        name: 'story_points',
        type: 'number',
        required: false,
        description: 'Story points',
      },
      {
        name: 'priority',
        type: 'string',
        required: false,
        description: 'Prioridad',
      },
    ],
  },
  {
    name: 'create-task',
    service: 'jira',
    method: 'POST',
    path: '/tools/create-task',
    description: 'Crear una tarea',
    category: 'Create',
    slashCommand: '/create-task',
    paramsIn: 'body',
    params: [
      {
        name: 'project',
        type: 'string',
        required: true,
        description: 'Key del proyecto',
      },
      {
        name: 'summary',
        type: 'string',
        required: true,
        description: 'Título',
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Descripción',
      },
      {
        name: 'priority',
        type: 'string',
        required: false,
        description: 'Prioridad',
      },
    ],
  },
  {
    name: 'create-bug',
    service: 'jira',
    method: 'POST',
    path: '/tools/create-bug',
    description: 'Crear un bug',
    category: 'Create',
    slashCommand: '/create-bug',
    paramsIn: 'body',
    params: [
      {
        name: 'project',
        type: 'string',
        required: true,
        description: 'Key del proyecto',
      },
      {
        name: 'summary',
        type: 'string',
        required: true,
        description: 'Título',
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Descripción',
      },
      {
        name: 'priority',
        type: 'string',
        required: false,
        description: 'Prioridad',
      },
    ],
  },
  {
    name: 'create-subtask',
    service: 'jira',
    method: 'POST',
    path: '/tools/create-subtask',
    description: 'Crear una subtarea',
    category: 'Create',
    slashCommand: '/create-subtask',
    paramsIn: 'body',
    params: [
      {
        name: 'parent',
        type: 'string',
        required: true,
        description: 'Key del issue padre',
      },
      {
        name: 'summary',
        type: 'string',
        required: true,
        description: 'Título',
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Descripción',
      },
    ],
  },
  // Update
  {
    name: 'update-issue',
    service: 'jira',
    method: 'PUT',
    path: '/tools/update-issue',
    description: 'Actualizar un issue',
    category: 'Update',
    slashCommand: '/update-issue',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'summary',
        type: 'string',
        required: false,
        description: 'Nuevo título',
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Nueva descripción',
      },
    ],
  },
  {
    name: 'transition-issue',
    service: 'jira',
    method: 'POST',
    path: '/tools/transition-issue',
    description: 'Transicionar un issue',
    category: 'Update',
    slashCommand: '/transition',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'transition_id',
        type: 'string',
        required: true,
        description: 'ID de la transición',
      },
    ],
  },
  {
    name: 'assign-issue',
    service: 'jira',
    method: 'POST',
    path: '/tools/assign-issue',
    description: 'Asignar un issue',
    category: 'Update',
    slashCommand: '/assign',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'account_id',
        type: 'string',
        required: true,
        description: 'Account ID del usuario',
      },
    ],
  },
  {
    name: 'set-story-points',
    service: 'jira',
    method: 'POST',
    path: '/tools/set-story-points',
    description: 'Establecer story points',
    category: 'Update',
    slashCommand: '/set-points',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'story_points',
        type: 'number',
        required: true,
        description: 'Puntos',
      },
    ],
  },
  {
    name: 'add-labels',
    service: 'jira',
    method: 'POST',
    path: '/tools/add-labels',
    description: 'Agregar labels a un issue',
    category: 'Update',
    slashCommand: '/add-labels',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'labels',
        type: 'string[]',
        required: true,
        description: 'Labels a agregar',
      },
    ],
  },
  // Delete
  {
    name: 'delete-issue',
    service: 'jira',
    method: 'POST',
    path: '/tools/delete-issue',
    description: 'Eliminar un issue',
    category: 'Update',
    slashCommand: '/delete-issue',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue a eliminar',
        example: 'PIAE-10',
      },
    ],
  },
  // Links
  {
    name: 'link-to-epic',
    service: 'jira',
    method: 'POST',
    path: '/tools/link-to-epic',
    description: 'Vincular issue a una épica',
    category: 'Links',
    slashCommand: '/link-epic',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'epic_key',
        type: 'string',
        required: true,
        description: 'Key de la épica',
      },
    ],
  },
  {
    name: 'link-issues',
    service: 'jira',
    method: 'POST',
    path: '/tools/link-issues',
    description: 'Vincular dos issues',
    category: 'Links',
    slashCommand: '/link-issues',
    paramsIn: 'body',
    params: [
      {
        name: 'inward_issue',
        type: 'string',
        required: true,
        description: 'Issue origen',
      },
      {
        name: 'outward_issue',
        type: 'string',
        required: true,
        description: 'Issue destino',
      },
      {
        name: 'link_type',
        type: 'string',
        required: true,
        description: 'Tipo de link',
        example: 'Relates',
      },
    ],
  },
  {
    name: 'set-parent',
    service: 'jira',
    method: 'POST',
    path: '/tools/set-parent',
    description: 'Establecer parent de un issue',
    category: 'Links',
    slashCommand: '/set-parent',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'parent_key',
        type: 'string',
        required: true,
        description: 'Key del parent',
      },
    ],
  },
  // Comments & Worklog
  {
    name: 'add-comment',
    service: 'jira',
    method: 'POST',
    path: '/tools/add-comment',
    description: 'Agregar comentario a un issue',
    category: 'Comments',
    slashCommand: '/comment',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'comment_text',
        type: 'string',
        required: true,
        description: 'Texto del comentario',
      },
    ],
  },
  {
    name: 'add-worklog',
    service: 'jira',
    method: 'POST',
    path: '/tools/add-worklog',
    description: 'Agregar worklog a un issue',
    category: 'Worklog',
    slashCommand: '/worklog',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_key',
        type: 'string',
        required: true,
        description: 'Key del issue',
      },
      {
        name: 'time_spent',
        type: 'string',
        required: true,
        description: 'Tiempo',
        example: '2h',
      },
      {
        name: 'comment',
        type: 'string',
        required: false,
        description: 'Comentario',
      },
    ],
  },
  // Bulk
  {
    name: 'bulk-assign',
    service: 'jira',
    method: 'POST',
    path: '/tools/bulk-assign',
    description: 'Asignar múltiples issues',
    category: 'Bulk',
    slashCommand: '/bulk-assign',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_keys',
        type: 'string[]',
        required: true,
        description: 'Keys de los issues',
      },
      {
        name: 'account_id',
        type: 'string',
        required: true,
        description: 'Account ID',
      },
    ],
  },
  {
    name: 'bulk-transition',
    service: 'jira',
    method: 'POST',
    path: '/tools/bulk-transition',
    description: 'Transicionar múltiples issues',
    category: 'Bulk',
    slashCommand: '/bulk-transition',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_keys',
        type: 'string[]',
        required: true,
        description: 'Keys de los issues',
      },
      {
        name: 'transition_id',
        type: 'string',
        required: true,
        description: 'ID de la transición',
      },
    ],
  },
  {
    name: 'bulk-add-labels',
    service: 'jira',
    method: 'POST',
    path: '/tools/bulk-add-labels',
    description: 'Agregar labels a múltiples issues',
    category: 'Bulk',
    slashCommand: '/bulk-labels',
    paramsIn: 'body',
    params: [
      {
        name: 'issue_keys',
        type: 'string[]',
        required: true,
        description: 'Keys de los issues',
      },
      {
        name: 'labels',
        type: 'string[]',
        required: true,
        description: 'Labels',
      },
    ],
  },
  // Admin
  {
    name: 'get-projects',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-projects',
    description: 'Listar proyectos',
    category: 'Admin',
    slashCommand: '/projects',
    paramsIn: 'query',
    params: [],
  },
  {
    name: 'get-project',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-project',
    description: 'Obtener un proyecto',
    category: 'Admin',
    slashCommand: '/project',
    paramsIn: 'query',
    params: [
      {
        name: 'project_key',
        type: 'string',
        required: true,
        description: 'Key del proyecto',
      },
    ],
  },
  {
    name: 'get-workflows',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-workflows',
    description: 'Listar workflows de Jira',
    category: 'Workflows',
    slashCommand: '/workflows',
    paramsIn: 'query',
    params: [],
  },
  {
    name: 'get-workflow-scheme',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-workflow-scheme',
    description:
      'Obtener un workflow scheme por ID, incluyendo workflow default y mapeo de issue types',
    category: 'Workflows',
    slashCommand: '/workflow-scheme',
    paramsIn: 'query',
    params: [
      {
        name: 'scheme_id',
        type: 'number',
        required: true,
        description: 'ID del workflow scheme',
      },
    ],
  },
  {
    name: 'get-workflow-details',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-workflow-details',
    description:
      'Obtener definición detallada de un workflow (statuses, transiciones, reglas)',
    category: 'Workflows',
    slashCommand: '/workflow-details',
    paramsIn: 'query',
    params: [
      {
        name: 'workflow_name',
        type: 'string',
        required: true,
        description: 'Nombre del workflow',
      },
    ],
  },
  {
    name: 'get-issue-types',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-issue-types',
    description: 'Listar tipos de issue',
    category: 'Admin',
    slashCommand: '/issue-types',
    paramsIn: 'query',
    params: [
      {
        name: 'project_key',
        type: 'string',
        required: true,
        description: 'Key del proyecto',
      },
    ],
  },
  {
    name: 'get-priorities',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-priorities',
    description: 'Listar prioridades',
    category: 'Admin',
    slashCommand: '/priorities',
    paramsIn: 'query',
    params: [],
  },
  {
    name: 'get-statuses',
    service: 'jira',
    method: 'GET',
    path: '/tools/get-statuses',
    description: 'Listar estados',
    category: 'Admin',
    slashCommand: '/statuses',
    paramsIn: 'query',
    params: [],
  },
  {
    name: 'create-project',
    service: 'jira',
    method: 'POST',
    path: '/tools/create-project',
    description: 'Crear un nuevo proyecto en Jira',
    category: 'Admin',
    slashCommand: '/create-project',
    paramsIn: 'body',
    params: [
      {
        name: 'name',
        type: 'string',
        required: true,
        description: 'Nombre completo del proyecto',
      },
      {
        name: 'key',
        type: 'string',
        required: true,
        description: 'Key único del proyecto (mayúsculas, ej: MYPROJ)',
      },
      {
        name: 'project_type_key',
        type: 'string',
        required: false,
        description: 'Tipo: software, business, service_desk',
        example: 'software',
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Descripción del proyecto',
      },
      {
        name: 'lead_account_id',
        type: 'string',
        required: false,
        description: 'Account ID del líder del proyecto',
      },
    ],
  },
  {
    name: 'delete-project',
    service: 'jira',
    method: 'POST',
    path: '/tools/delete-project',
    description: 'Eliminar permanentemente un proyecto y todos sus issues',
    category: 'Admin',
    slashCommand: '/delete-project',
    paramsIn: 'body',
    params: [
      {
        name: 'project_key',
        type: 'string',
        required: true,
        description: 'Key del proyecto a eliminar',
      },
    ],
  },
  {
    name: 'health-check',
    service: 'jira',
    method: 'POST',
    path: '/tools/health-check',
    description: 'Health check del servidor MCP',
    category: 'Admin',
    slashCommand: '/health-check',
    paramsIn: 'body',
    params: [
      {
        name: 'echo',
        type: 'string',
        required: false,
        description: 'Mensaje opcional para eco',
      },
    ],
  },
]

// ─── Slack Tools ──────────────────────────────────────────────────────────────

const slackTools: McpTool[] = [
  {
    name: 'slack_get_channels',
    service: 'slack',
    method: 'POST',
    path: '/slack/channels',
    description: 'Listar canales de Slack del workspace',
    category: 'Channels',
    slashCommand: '/slack-channels',
    paramsIn: 'body',
    params: [],
  },
  {
    name: 'slack_read_channel',
    service: 'slack',
    method: 'POST',
    path: '/slack/history',
    description: 'Leer mensajes recientes de un canal de Slack',
    category: 'Messages',
    slashCommand: '/slack-read',
    paramsIn: 'body',
    params: [
      {
        name: 'channel_id',
        type: 'string',
        required: true,
        description: 'ID del canal de Slack',
        example: 'C12345678',
      },
      {
        name: 'limit',
        type: 'number',
        required: false,
        description: 'Cantidad de mensajes a obtener (default 30)',
        example: '30',
      },
    ],
  },
  {
    name: 'slack_send_message',
    service: 'slack',
    method: 'POST',
    path: '/slack/send',
    description: 'Enviar un mensaje a un canal de Slack',
    category: 'Messages',
    slashCommand: '/slack-send',
    paramsIn: 'body',
    params: [
      {
        name: 'channel_id',
        type: 'string',
        required: true,
        description: 'ID del canal de Slack',
        example: 'C12345678',
      },
      {
        name: 'text',
        type: 'string',
        required: true,
        description: 'Texto del mensaje a enviar',
      },
    ],
  },
  {
    name: 'slack_get_users',
    service: 'slack',
    method: 'POST',
    path: '/slack/users',
    description: 'Listar usuarios del workspace de Slack',
    category: 'Users',
    slashCommand: '/slack-users',
    paramsIn: 'body',
    params: [],
  },
  {
    name: 'slack_get_channel_by_name',
    service: 'slack',
    method: 'POST',
    path: '/slack/channel-by-name',
    description: 'Buscar un canal de Slack por nombre para obtener su ID',
    category: 'Channels',
    slashCommand: '/slack-channel',
    paramsIn: 'body',
    params: [
      {
        name: 'channel_name',
        type: 'string',
        required: true,
        description: 'Nombre del canal (sin #)',
        example: 'general',
      },
    ],
  },
]

// ─── Google Tools ─────────────────────────────────────────────────────────────

const googleTools: McpTool[] = [
  {
    name: 'google_create_doc',
    service: 'google',
    method: 'POST',
    path: '/google/create-doc',
    description: 'Crear un Google Doc con título y contenido',
    category: 'Docs',
    slashCommand: '/create-doc',
    paramsIn: 'body',
    params: [
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Título del documento',
        example: 'Sprint 23 Review',
      },
      {
        name: 'content',
        type: 'string',
        required: true,
        description: 'Contenido del documento (texto plano o markdown)',
      },
    ],
  },
  {
    name: 'google_create_sheet',
    service: 'google',
    method: 'POST',
    path: '/google/create-sheet',
    description: 'Crear un Google Sheet con título y datos',
    category: 'Sheets',
    slashCommand: '/create-sheet',
    paramsIn: 'body',
    params: [
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Título del spreadsheet',
        example: 'Backlog PIAE',
      },
      {
        name: 'headers',
        type: 'string[]',
        required: false,
        description: 'Encabezados de columnas',
        example: 'Key,Summary,Status,Priority',
      },
      {
        name: 'rows',
        type: 'string',
        required: false,
        description: 'Filas de datos en formato JSON array de arrays',
      },
    ],
  },
  {
    name: 'google_create_slide',
    service: 'google',
    method: 'POST',
    path: '/google/create-slide',
    description: 'Crear una presentación de Google Slides',
    category: 'Slides',
    slashCommand: '/create-slide',
    paramsIn: 'body',
    params: [
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Título de la presentación',
        example: 'Sprint 23 Demo',
      },
      {
        name: 'slides',
        type: 'string',
        required: false,
        description: 'Contenido de slides en formato JSON: [{title, body}]',
      },
    ],
  },
]

// ─── Notion Tools ─────────────────────────────────────────────────────────────

const notionTools: McpTool[] = [
  {
    name: 'search',
    service: 'notion',
    method: 'POST',
    path: '/tool/search',
    description: 'Buscar páginas y bases de datos en Notion',
    category: 'Search',
    slashCommand: '/notion-search',
    paramsIn: 'body',
    params: [
      {
        name: 'query',
        type: 'string',
        required: false,
        description: 'Texto a buscar',
      },
      {
        name: 'filter',
        type: 'string',
        required: false,
        description: 'Filtro (ej: { })',
      },
    ],
  },
  {
    name: 'notion_list_databases',
    service: 'notion',
    method: 'POST',
    path: '/tool/list_databases',
    description: 'Listar bases de datos del workspace de Notion',
    category: 'Databases',
    slashCommand: '/notion-databases',
    paramsIn: 'body',
    params: [],
  },
  {
    name: 'notion_list_pages',
    service: 'notion',
    method: 'POST',
    path: '/tool/list_pages',
    description: 'Listar páginas del workspace de Notion',
    category: 'Pages',
    slashCommand: '/notion-pages',
    paramsIn: 'body',
    params: [],
  },
  {
    name: 'notion_get_page',
    service: 'notion',
    method: 'POST',
    path: '/tool/get_page',
    description: 'Obtener propiedades de una página de Notion',
    category: 'Pages',
    slashCommand: '/notion-page',
    paramsIn: 'body',
    params: [
      {
        name: 'page_id',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
    ],
  },
  {
    name: 'notion_create_page',
    service: 'notion',
    method: 'POST',
    path: '/tool/create_page',
    description: 'Crear una nueva página en Notion',
    category: 'Pages',
    slashCommand: '/notion-create-page',
    paramsIn: 'body',
    params: [
      {
        name: 'parent',
        type: 'string',
        required: true,
        description: 'Referencia al parent (database_id o page_id)',
      },
      {
        name: 'properties',
        type: 'string',
        required: true,
        description: 'Propiedades de la página en JSON',
      },
    ],
  },
  {
    name: 'notion_update_page',
    service: 'notion',
    method: 'POST',
    path: '/tool/update_page',
    description: 'Actualizar propiedades de una página de Notion',
    category: 'Pages',
    slashCommand: '/notion-update-page',
    paramsIn: 'body',
    params: [
      {
        name: 'page_id',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
      {
        name: 'properties',
        type: 'string',
        required: true,
        description: 'Propiedades actualizadas en JSON',
      },
    ],
  },
  {
    name: 'notion_archive_page',
    service: 'notion',
    method: 'POST',
    path: '/tool/archive_page',
    description: 'Archivar una página de Notion',
    category: 'Pages',
    slashCommand: '/notion-archive',
    paramsIn: 'body',
    params: [
      {
        name: 'page_id',
        type: 'string',
        required: true,
        description: 'ID de la página',
      },
    ],
  },
  {
    name: 'notion_get_database',
    service: 'notion',
    method: 'POST',
    path: '/tool/get_database',
    description: 'Obtener esquema de una base de datos de Notion',
    category: 'Databases',
    slashCommand: '/notion-database',
    paramsIn: 'body',
    params: [
      {
        name: 'database_id',
        type: 'string',
        required: true,
        description: 'ID de la base de datos',
      },
    ],
  },
  {
    name: 'notion_query_database',
    service: 'notion',
    method: 'POST',
    path: '/tool/query_database',
    description: 'Consultar una base de datos de Notion con filtros',
    category: 'Databases',
    slashCommand: '/notion-query',
    paramsIn: 'body',
    params: [
      {
        name: 'database_id',
        type: 'string',
        required: true,
        description: 'ID de la base de datos',
      },
      {
        name: 'filter',
        type: 'string',
        required: false,
        description: 'Filtro en JSON',
      },
      {
        name: 'sorts',
        type: 'string',
        required: false,
        description: 'Ordenamiento en JSON',
      },
    ],
  },
  {
    name: 'notion_get_block_children',
    service: 'notion',
    method: 'POST',
    path: '/tool/get_block_children',
    description: 'Obtener bloques hijos de una página o bloque de Notion',
    category: 'Blocks',
    slashCommand: '/notion-blocks',
    paramsIn: 'body',
    params: [
      {
        name: 'block_id',
        type: 'string',
        required: true,
        description: 'ID del bloque o página',
      },
    ],
  },
  {
    name: 'notion_append_blocks',
    service: 'notion',
    method: 'POST',
    path: '/tool/append_blocks',
    description: 'Agregar bloques de contenido a una página de Notion',
    category: 'Blocks',
    slashCommand: '/notion-append',
    paramsIn: 'body',
    params: [
      {
        name: 'block_id',
        type: 'string',
        required: true,
        description: 'ID del bloque padre',
      },
      {
        name: 'children',
        type: 'string',
        required: true,
        description: 'Bloques a agregar en JSON',
      },
    ],
  },
  {
    name: 'notion_health_check',
    service: 'notion',
    method: 'POST',
    path: '/tool/health_check',
    description: 'Verificar conectividad con Notion API',
    category: 'System',
    slashCommand: '/notion-health',
    paramsIn: 'body',
    params: [
      {
        name: 'echo',
        type: 'string',
        required: false,
        description: 'Mensaje de eco',
      },
    ],
  },
]

// ─── Execute MCP Tool (Generic) ──────────────────────────────────────────────

const executeMcpTool: McpTool[] = [
  {
    name: 'execute_mcp_tool',
    service: 'google-mcp',
    method: 'POST',
    path: '/tool/{tool_name}',
    description:
      'Ejecuta una herramienta de un servicio extendido (Gmail, Calendar, Drive, Docs, Sheets, Forms, Tasks, Contacts, Chat). Consultá la lista de herramientas extendidas en el prompt del sistema.',
    category: 'Generic',
    slashCommand: '/mcp-exec',
    paramsIn: 'body',
    params: [
      {
        name: 'tool_name',
        type: 'string',
        required: true,
        description: 'Nombre exacto de la herramienta a ejecutar',
        example: 'gmail_list_messages',
      },
      {
        name: 'service',
        type: 'string',
        required: true,
        description:
          'Servicio al que pertenece (gmail, calendar, drive, docs, sheets, forms, tasks, contacts, chat, slack)',
        example: 'gmail',
      },
      {
        name: 'params',
        type: 'string',
        required: true,
        description: 'Parámetros de la herramienta en formato JSON',
      },
    ],
  },
]

// ─── System commands ──────────────────────────────────────────────────────────

export interface SystemCommand {
  slashCommand: string
  description: string
  category: 'System'
}
export const systemCommands: SystemCommand[] = [
  {
    slashCommand: '/help',
    description: 'Ver todos los comandos disponibles',
    category: 'System',
  },
  {
    slashCommand: '/status',
    description: 'Estado de conexión (tokens)',
    category: 'System',
  },
  {
    slashCommand: '/clear',
    description: 'Limpiar chat',
    category: 'System',
  },
  {
    slashCommand: '/refresh-tokens',
    description: 'Refrescar tokens de Confluence y Jira',
    category: 'System',
  },
]

// ─── Exports ──────────────────────────────────────────────────────────────────

export const allTools: McpTool[] = [
  ...confluenceTools,
  ...jiraTools,
  ...slackTools,
  ...googleTools,
  ...notionTools,
  ...executeMcpTool,
]

// Aliases: old tool names → new agile-prefixed names
const TOOL_ALIASES: Record<string, string> = {
  'get-boards': 'agile-get-boards',
  'get-sprints': 'agile-get-sprints',
  'get-sprint': 'agile-get-sprint',
  'get-sprint-issues': 'agile-get-sprint-issues',
  'get-active-sprints': 'agile-get-active-sprints',
  'get-backlog': 'agile-get-backlog',
}

/**
 * Find a tool by name. Handles:
 * 1. Exact match (e.g. 'search-issues')
 * 2. Underscore→hyphen normalization (e.g. 'search_issues' → 'search-issues')
 * 3. Legacy aliases (e.g. 'get-boards' → 'agile-get-boards')
 * This is needed because the MCP server returns tool names with underscores
 * but our catalog uses hyphens (matching the REST API paths).
 */
export function findToolByName(name: string): McpTool | undefined {
  // 1. Exact match
  const exact = allTools.find((t) => t.name === name)
  if (exact) return exact

  // 2. Try alias
  const aliased = TOOL_ALIASES[name]
  if (aliased) {
    const found = allTools.find((t) => t.name === aliased)
    if (found) return found
  }

  // 3. Normalize underscores to hyphens (MCP server uses underscores, catalog uses hyphens)
  const normalized = name.replace(/_/g, '-')
  if (normalized !== name) {
    const found = allTools.find((t) => t.name === normalized)
    if (found) return found
    // Also try alias on normalized name
    const aliasedNorm = TOOL_ALIASES[normalized]
    if (aliasedNorm) return allTools.find((t) => t.name === aliasedNorm)
  }
  return undefined
}
export function findToolBySlashCommand(cmd: string): McpTool | undefined {
  return allTools.find((t) => t.slashCommand === cmd)
}
export function getToolsByService(service: McpService): McpTool[] {
  return allTools.filter((t) => t.service === service)
}
export function getToolsByCategory(
  service: McpService,
  category: string,
): McpTool[] {
  return allTools.filter(
    (t) => t.service === service && t.category === category,
  )
}
export function getCategories(service: McpService): string[] {
  const categories = allTools
    .filter((t) => t.service === service)
    .map((t) => t.category)
  return Array.from(new Set(categories))
}

/** Get all slash commands for the palette (tools + system) */
export function getAllSlashCommands(): Array<string> {
  const toolCommands = allTools.map((t) => t.slashCommand)
  const systemCommandsList = systemCommands.map((c) => c.slashCommand)
  return [...toolCommands, ...systemCommandsList]
}

// ─── Dynamic Tool Discovery ──────────────────────────────────────────────────

/** Get a summary of available tool capabilities grouped by service */
export function getToolCapabilitySummary(): Record<string, string[]> {
  const summary: Record<string, string[]> = {}
  for (const tool of allTools) {
    if (!summary[tool.service]) {
      summary[tool.service] = []
    }
    summary[tool.service].push(tool.name)
  }
  return summary
}

/** Get available services (for dynamic prompt building) */
export function getAvailableServices(): McpService[] {
  return [...new Set(allTools.map((t) => t.service))]
}

/** Check if a specific capability exists in the tool catalog */
export function hasCapability(keyword: string): boolean {
  const lower = keyword.toLowerCase()
  return allTools.some((t) => t.name.toLowerCase().includes(lower))
}

/** Find tools that might match a user intent */
export function findToolsForIntent(intent: string): McpTool[] {
  const lower = intent.toLowerCase()
  return allTools.filter(
    (t) =>
      t.name.toLowerCase().includes(lower) ||
      t.description.toLowerCase().includes(lower),
  )
}

/** Get tool count grouped by classifier service ID */
export function getToolCountByService(
  enabledServices: string[],
): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const service of enabledServices) {
    counts[service] = allTools.filter((t) => t.service === service).length
  }
  return counts
}

/** Get tools filtered by core service names (for full schema injection) */
export function getToolsForCoreServices(services: string[]): McpTool[] {
  const set = new Set(services)
  return allTools.filter((t) => set.has(t.service))
}
